import { useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { MdClose, MdLocalOffer, MdOutlineRemoveCircle } from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import { DarkModeContext } from "../Context/DarkModeContext.jsx";

const PRESETS = [10, 25, 50, 100];

export default function ManageDiscountModal({
  open,
  count,
  removableCount = count,
  percentage,
  onPercentageChange,
  onApply,
  onRemove,
  onClose,
  loading = false,
}) {
  const { isDarkMode } = useContext(DarkModeContext);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !loading) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [loading, onClose, open]);

  const handlePercentageChange = (value) => {
    if (value === "") {
      onPercentageChange("");
      return;
    }

    onPercentageChange(String(Math.min(100, Number(value))));
  };

  return createPortal(
    <AnimatePresence>
      {open && (
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !loading) onClose();
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 18 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          transition={{ type: "spring", stiffness: 360, damping: 28 }}
          className={`w-full max-w-lg overflow-y-auto rounded-3xl border shadow-2xl  max-h-[calc(100dvh-2rem)] ${
            isDarkMode
              ? "border-white/10 bg-[#191919] text-white"
              : "border-slate-200 bg-white text-slate-900"
          } `}
          role="dialog"
          aria-modal="true"
          aria-labelledby="manage-discount-title"
        >
        <div className="flex items-start justify-between gap-4 px-6 pb-5 pt-6 sm:px-7 sm:pt-7">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-500">
              <MdLocalOffer size={25} aria-hidden="true" />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 id="manage-discount-title" className="text-xl font-bold">
                  Manage discount
                </h2>
                <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-bold text-amber-600">
                  {count} selected
                </span>
              </div>
              <p className={`mt-1 text-sm ${isDarkMode ? "text-gray-400" : "text-slate-500"}`}>
                Apply one percentage discount to the selected {count === 1 ? "course" : "courses"}.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Close discount modal"
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition disabled:opacity-50 ${
              isDarkMode ? "text-gray-400 hover:bg-white/10 hover:text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            <MdClose size={22} />
          </button>
        </div>

        <div className="px-6 pb-7 sm:px-7">
          <label htmlFor="bulkDiscountPercentage" className="block text-sm font-semibold">
            Discount percentage
          </label>
          <div className="relative mt-2">
            <input
              id="bulkDiscountPercentage"
              type="number"
              min="1"
              max="100"
              step="0.01"
              value={percentage}
              onChange={(event) => handlePercentageChange(event.target.value)}
              placeholder="0"
              autoFocus
              className={`w-full rounded-2xl border px-4 py-3.5 pr-12 text-lg font-semibold outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 ${
                isDarkMode
                  ? "border-white/10 bg-white/[0.04] text-white"
                  : "border-slate-200 bg-slate-50 text-slate-900"
              }`}
            />
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-lg font-bold text-amber-500">
              %
            </span>
          </div>

          <div className="mt-3 flex gap-2" aria-label="Discount presets">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => handlePercentageChange(String(preset))}
                disabled={loading}
                className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition disabled:opacity-50 ${
                  Number(percentage) === preset
                    ? "border-amber-500 bg-amber-500 text-white"
                    : isDarkMode
                      ? "border-white/10 hover:border-amber-500/60 hover:bg-amber-500/10"
                      : "border-slate-200 hover:border-amber-400 hover:bg-amber-50"
                }`}
              >
                {preset}%
              </button>
            ))}
          </div>
        </div>

        <div className={`flex flex-col-reverse gap-3 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 ${isDarkMode ? "border-white/10" : "border-slate-100"}`}>
          <button
            type="button"
            onClick={onRemove}
            disabled={loading || removableCount === 0}
            title={removableCount === 0 ? "Selected courses have no discount" : undefined}
            className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-red-500 transition disabled:opacity-50 ${isDarkMode ? "hover:bg-red-500/10" : "hover:bg-red-50"}`}
          >
            <MdOutlineRemoveCircle size={19} />
            Remove {removableCount <= 1 ? "discount" : "discounts"}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:opacity-50 sm:flex-none ${isDarkMode ? "bg-white/10 hover:bg-white/15" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              disabled={loading}
              className="flex-1 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600 disabled:cursor-wait disabled:opacity-60 sm:flex-none"
            >
              {loading ? "Saving…" : "Apply discount"}
            </button>
          </div>
        </div>
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
